import rootStore from "@/app/store";
import crud, { ApiResponse } from "../crud";

export interface Ticket {
  id: number;
  subject: string;
  type: TicketCategoryType;
  status: TicketStatus;
  priority: TicketPriority;
  created_at: string;
  closed_at: string;
  category: number;
  office_id: number;
  agreement_id: number;
  renter_id: number;
  creator_user_id: number;
  creator_user_type: "user" | "landlord";
  visible_to_company: boolean;
}

interface TicketCreate extends Partial<Ticket> {}
interface TicketUpdate extends Partial<Ticket> {}

export interface TicketMessage {
  id: number;
  ticket_id: number;
  creator_user_id: number;
  creator_user_type: "user" | "landlord";
  creator_user_name: string;
  content: string;
  attachments: string[];
  created_at: string;
}

interface CreateTicketMessage {
  content: string;
  attachments?: string[];
}

export interface TicketCategory {
  id: number;
  display_name: string;
  type: TicketCategoryType;
}

export type TicketCategoryType = "user_to_landlord" | "to_platform";
export type TicketPriority = "low" | "medium" | "high" | "urgent";
export type TicketStatus = "open" | "in_progress" | "closed";

export function TicketsAPI(baseUrl: string) {
  return {
    list: async (
      ticket_type: TicketCategoryType,
      status?: TicketStatus,
      priority?: TicketPriority,
    ) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      const params = new URLSearchParams();

      if (status) params.append("status", status);
      if (priority) params.append("priority", priority);

      return (await crud.get(
        `${baseUrl}/tickets/${ticket_type}?business_profile_id=${businessProfileId}&${params.toString()}`,
        undefined,
        true,
      )) as ApiResponse<Ticket[]>;
    },
    get: async (type: string, id: number) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.get(
        `${baseUrl}/tickets/${type}/${id}?business_profile_id=${businessProfileId}`,
        undefined,
        true,
      )) as ApiResponse<Ticket>;
    },
    create: async (userData: TicketCreate) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.post(
        `${baseUrl}/tickets/to_platform/?business_profile_id=${businessProfileId}`,
        userData,
        true,
      )) as ApiResponse<Ticket>;
    },
    update: async (
      type: TicketCategoryType,
      id: number,
      userData: TicketUpdate,
    ) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.put(
        `${baseUrl}/tickets/${type}/${id}?business_profile_id=${businessProfileId}`,
        userData,
        true,
      )) as ApiResponse<Ticket>;
    },

    messages: {
      list: async (type: TicketCategoryType, id: number) => {
        const businessProfileId =
          rootStore.getState().businessProfile.businessProfile.id;
        return (await crud.get(
          `${baseUrl}/tickets/${type}/${id}/messages?business_profile_id=${businessProfileId}`,
          undefined,
          true,
        )) as ApiResponse<TicketMessage[]>;
      },
      create: async (
        type: string,
        id: number,
        messageData: CreateTicketMessage,
      ) => {
        const businessProfileId =
          rootStore.getState().businessProfile.businessProfile.id;
        return (await crud.post(
          `${baseUrl}/tickets/${type}/${id}/messages?business_profile_id=${businessProfileId}`,
          messageData,
          true,
        )) as ApiResponse<TicketMessage>;
      },
    },

    categories: {
      list: async (type: TicketCategoryType) => {
        const businessProfileId =
          rootStore.getState().businessProfile.businessProfile.id;
        return (await crud.get(
          `${baseUrl}/tickets/${type}/categories/?business_profile_id=${businessProfileId}`,
          undefined,
          true,
        )) as ApiResponse<TicketCategory[]>;
      },
    },
  };
}
